
import { useState } from "react";
import { Page, PageType, PagePlatform } from "../types/page";
import { getDefaultTitle, getDefaultContent } from "../utils/pageUtils";
import { useToast } from "@/components/ui/use-toast";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";

export function usePagesManager({ pages, setPages }: {
  pages: Page[];
  setPages: React.Dispatch<React.SetStateAction<Page[]>>;
}) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentEditPage, setCurrentEditPage] = useState<Page | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [showPrintPreview, setShowPrintPreview] = useState<boolean>(false);
  const { toast } = useToast();

  // Get enabled and disabled pages
  const enabledPages = pages
    .filter(page => page.enabled)
    .sort((a, b) => a.order - b.order);
  
  const disabledPages = pages
    .filter(page => !page.enabled)
    .sort((a, b) => a.order - b.order);

  const handleTogglePage = (id: string) => {
    setPages(prevPages => 
      prevPages.map(page => 
        page.id === id ? { ...page, enabled: !page.enabled } : page
      )
    );
  };

  const handlePlatformChange = (id: string, platform: PagePlatform) => {
    setPages(prevPages => 
      prevPages.map(page => 
        page.id === id ? { ...page, platform } : page
      )
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveDragId(null);
    
    if (!over) return;
    
    const activeId = active.id as string;
    const overId = over.id as string;
    
    if (activeId === overId) return;

    // Only allow reordering within enabled pages
    const activeIndex = enabledPages.findIndex(page => page.id === activeId);
    const overIndex = enabledPages.findIndex(page => page.id === overId);
    
    if (activeIndex === -1 || overIndex === -1) return;
    
    // Reorder enabled pages
    const reorderedPages = arrayMove(enabledPages, activeIndex, overIndex);
    
    // Update order numbers for enabled pages
    const updatedEnabledPages = reorderedPages.map((page, index) => ({
      ...page,
      order: index + 1
    }));
    
    // Update the pages array
    setPages(prevPages => {
      const disabledPagesUnchanged = prevPages.filter(page => !page.enabled);
      return [...disabledPagesUnchanged, ...updatedEnabledPages];
    });
  };

  const handleCreatePage = (type: PageType) => {
    const newPage: Page = {
      id: Date.now().toString(),
      title: getDefaultTitle(type),
      type: type,
      content: getDefaultContent(type),
      enabled: true,
      order: enabledPages.length + 1,
      platform: "both", // Default to showing on both platforms
      position: "fullscreen"  // All pages are now fullscreen
    };
    
    setCurrentEditPage(newPage);
    setIsEditing(true);
  };

  const handleSavePage = (page: Page) => {
    if (pages.some(p => p.id === page.id)) {
      setPages(prevPages => 
        prevPages.map(p => p.id === page.id ? page : p)
      );
    } else {
      setPages(prevPages => [...prevPages, page]);
    }
    
    setIsEditing(false);
    setCurrentEditPage(null);
    
    toast({
      title: "השינויים נשמרו",
      description: `הדף "${page.title}" נשמר בהצלחה.`,
    });
  };

  const handleDuplicatePage = (page: Page) => {
    const duplicatedPage: Page = {
      ...page,
      id: Date.now().toString(),
      title: `${page.title} (העתק)`,
      order: enabledPages.length + 1,
      position: "fullscreen"
    };
    
    setPages(prevPages => [...prevPages, duplicatedPage]);
    
    toast({
      title: "הדף שוכפל",
      description: `הדף "${page.title}" שוכפל בהצלחה.`,
    });
  };

  const handleEditPage = (page: Page) => {
    setCurrentEditPage(page);
    setIsEditing(true);
  };

  const handleDeletePage = (id: string) => {
    if (window.confirm("האם אתה בטוח שברצונך למחוק דף זה?")) {
      setPages(prevPages => prevPages.filter(page => page.id !== id));
    }
  };

  const handlePrintTimesPages = (printRef: React.RefObject<HTMLDivElement>) => {
    // Check if there are any Times pages
    const hasTimesPages = pages.some(page => page.type === "times");
    
    if (!hasTimesPages) {
      toast({
        title: "אין דפי זמנים",
        description: "לא נמצאו דפי זמנים להדפסה.",
        variant: "destructive",
      });
      return;
    }
    
    setShowPrintPreview(true);
    
    // Use setTimeout to ensure the DOM is updated before printing
    setTimeout(() => {
      if (printRef.current) {
        const originalContents = document.body.innerHTML;
        const printContents = printRef.current.innerHTML;
        
        document.body.innerHTML = `
          <div class="print-only">${printContents}</div>
        `;
        
        window.print();
        document.body.innerHTML = originalContents;
        setShowPrintPreview(false);
      }
    }, 300);
  };

  return {
    isEditing,
    currentEditPage,
    showPrintPreview,
    activeDragId,
    enabledPages,
    disabledPages,
    handleTogglePage,
    handlePlatformChange,
    handleDragStart,
    handleDragEnd,
    handleCreatePage,
    handleSavePage,
    handleDuplicatePage,
    handleEditPage,
    handleDeletePage,
    handlePrintTimesPages,
    setIsEditing,
    setCurrentEditPage
  };
}
